/**
 * Social Media URL utilities
 * Smart parsing and validation for social media links
 */

/**
 * Parse Instagram username/URL and return full URL
 * Handles: @username, username, full URL
 */
export function parseInstagramUrl(input: string): string | null {
  if (!input) return null

  // Remove whitespace
  const trimmed = input.trim()

  // Already a full URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    // Validate it's actually Instagram
    if (trimmed.includes('instagram.com/')) {
      return trimmed
    }
    return null // Not an Instagram URL
  }

  // Remove @ if present
  const username = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed

  // Validate username (alphanumeric, dots, underscores only)
  if (!/^[a-zA-Z0-9._]+$/.test(username)) {
    return null
  }

  return `https://instagram.com/${username}`
}

/**
 * Parse Facebook username/URL and return full URL
 * Handles: username, facebook.com/username, full URL
 */
export function parseFacebookUrl(input: string): string | null {
  if (!input) return null

  const trimmed = input.trim()

  // Already a full URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    // Validate it's actually Facebook
    if (trimmed.includes('facebook.com/')) {
      return trimmed
    }
    return null
  }

  // Remove facebook.com/ prefix if present
  let username = trimmed
  if (trimmed.startsWith('facebook.com/')) {
    username = trimmed.replace('facebook.com/', '')
  }

  // Validate username
  if (!/^[a-zA-Z0-9.]+$/.test(username)) {
    return null
  }

  return `https://facebook.com/${username}`
}

/**
 * Parse Google Maps address and return Google Maps URL
 */
export function parseGoogleMapsUrl(address: string): string | null {
  if (!address) return null

  const trimmed = address.trim()

  // Already a Google Maps URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    if (trimmed.includes('google.com/maps') || trimmed.includes('maps.google.com')) {
      return trimmed
    }
    return null
  }

  // Create search query URL
  const encoded = encodeURIComponent(trimmed)
  return `https://www.google.com/maps/search/?api=1&query=${encoded}`
}

/**
 * Sanitize Instagram input on blur (form field)
 * Removes @ and rejects full URLs
 */
export function sanitizeInstagramInput(input: string): string {
  const trimmed = input.trim()

  // Reject URLs
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return '' // or show error
  }

  // Remove @
  return trimmed.startsWith('@') ? trimmed.slice(1) : trimmed
}

/**
 * Sanitize Facebook input on blur (form field)
 * Removes facebook.com/ prefix and rejects full URLs
 */
export function sanitizeFacebookInput(input: string): string {
  const trimmed = input.trim()

  // Reject full URLs (with http/https)
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return '' // or show error
  }

  // Remove facebook.com/ if present
  if (trimmed.startsWith('facebook.com/')) {
    return trimmed.replace('facebook.com/', '')
  }

  return trimmed
}
